import React from 'react';
import { Link } from 'react-router-dom';
import { Accordion } from "@/components/ui/accordion";
import { Button } from '@/components/ui/button';
import { HelpCircle, MessageCircle, BookOpen, Map } from 'lucide-react';
import { faqData } from '@/lib/faqData';
import { FaqItem } from '@/components/faq/FaqItem';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/seo/SEO';

const FaqPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background font-sans selection:bg-primary/20">
      <SEO title="SWPPP Software FAQ: Accounts, Inspections & Billing" description="Find answers to common RunOffTrack questions about inspection software, accounts, and billing, with links to support and field resources." canonical="/faq" />
      
      {/* Header */}
      <div className="bg-muted/30 py-20 border-b border-border text-center">
        <div className="container px-4">
           <div className="inline-flex items-center justify-center p-3 bg-primary/10 text-primary rounded-full mb-6">
             <HelpCircle size={32} />
           </div>
           <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">Frequently Asked Questions</h1>
           <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
             Everything you need to know about the product and billing.
           </p>
        </div>
      </div>

      {/* FAQ List */}
      <div className="container px-4 py-16 md:py-24 max-w-3xl mx-auto">
        <Accordion type="single" collapsible className="w-full space-y-4">
          {faqData.map((faq, index) => (
            <FaqItem key={index} item={faq} index={index} />
          ))}
        </Accordion>
      </div>

      {/* Resource Cross-Linking */}
      <div className="container px-4 pb-16 max-w-4xl mx-auto">
          <h3 className="text-xl font-bold mb-6 text-center">Looking for technical compliance info?</h3>
          <div className="grid md:grid-cols-2 gap-6">
              <Card className="hover:border-primary/50 transition-colors cursor-pointer" onClick={() => window.location.href='/resources/bmp-field-guide'}>
                  <CardContent className="p-6 flex items-center gap-4">
                      <div className="p-3 bg-green-100 dark:bg-green-900/30 text-green-700 rounded-lg">
                          <BookOpen size={24} />
                      </div>
                      <div>
                          <div className="font-semibold text-lg">BMP Field Guide</div>
                          <p className="text-sm text-muted-foreground">Inspection checklists for Silt Fences, Inlets, etc.</p>
                      </div>
                  </CardContent>
              </Card>
              <Card className="hover:border-primary/50 transition-colors cursor-pointer" onClick={() => window.location.href='/swppp-requirements'}>
                  <CardContent className="p-6 flex items-center gap-4">
                      <div className="p-3 bg-blue-100 dark:bg-blue-900/30 text-blue-700 rounded-lg">
                          <Map size={24} />
                      </div>
                      <div>
                          <div className="font-semibold text-lg">State Regulations</div>
                          <p className="text-sm text-muted-foreground">Permit details for your specific state.</p>
                      </div>
                  </CardContent>
              </Card>
          </div>
      </div>

      {/* Contact CTA */}
      <div className="container px-4 pb-24 text-center">
        <div className="bg-primary/5 border border-primary/10 rounded-2xl p-10 max-w-3xl mx-auto">
          <h3 className="text-2xl font-bold mb-3">Still have questions?</h3>
          <p className="text-muted-foreground mb-8">Can't find the answer you're looking for? Please chat to our friendly team.</p>
          <Button size="lg" className="gap-2" asChild>
            <Link to="/contact">
              <MessageCircle size={18} /> Get in Touch
            </Link>
          </Button>
        </div>
      </div>

    </div>
  );
};

export default FaqPage;
